import React, { useState, useEffect } from 'react';
import { FiUsers, FiFileText, FiActivity, FiTrendingUp, FiPlus, FiToggleLeft, FiToggleRight, FiX } from 'react-icons/fi';
import { superAdmin as superAdminApi } from '../../api';
import toast from 'react-hot-toast';

export function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    superAdminApi.getStats().then(r => setStats(r.data.data)).catch(() => toast.error('Failed to load'));
  }, []);

  const userStats = stats?.users || [];
  const getCnt = (role) => parseInt(userStats.find(u => u.role === role)?.count || 0);
  const examStats = stats?.exams || [];
  const getExamCnt = (status) => parseInt(examStats.find(e => e.status === status)?.count || 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-black text-gray-900">System Overview</h1>
        <p className="text-gray-500 text-sm">Platform-wide statistics and health</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: FiUsers, label: 'Candidates', value: getCnt('candidate'), color: 'bg-navy' },
          { icon: FiUsers, label: 'Admins', value: getCnt('admin'), color: 'bg-blue-500' },
          { icon: FiFileText, label: 'Total Exams', value: examStats.reduce((a, e) => a + parseInt(e.count), 0), color: 'bg-emerald-500' },
          { icon: FiActivity, label: 'Published Exams', value: getExamCnt('published'), color: 'bg-amber-500' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="stat-card">
            <div className={`w-11 h-11 rounded-xl ${color} flex items-center justify-center flex-shrink-0`}><Icon size={20} className="text-white" /></div>
            <div><p className="text-sm text-gray-500">{label}</p><p className="text-2xl font-black text-gray-900">{value ?? '—'}</p></div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-5">
          <h3 className="font-bold text-gray-900 mb-3">Exam Status Breakdown</h3>
          <div className="space-y-2">
            {examStats.map(e => (
              <div key={e.status} className="flex items-center justify-between text-sm">
                <span className="capitalize text-gray-600">{e.status}</span>
                <span className="font-bold text-gray-900">{e.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-5">
          <h3 className="font-bold text-gray-900 mb-3">Overall Results</h3>
          {stats?.results?.[0] && (
            <div className="space-y-3">
              <div className="flex justify-between text-sm"><span className="text-gray-500">Total Results</span><span className="font-bold">{stats.results[0].count}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">Average Score</span><span className="font-bold">{parseFloat(stats.results[0].avg || 0).toFixed(1)}%</span></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminsManagePage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '' });
  const [creating, setCreating] = useState(false);

  const load = () => {
    superAdminApi.getAdmins().then(r => setAdmins(r.data.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await superAdminApi.createAdmin(form);
      toast.success('Admin created');
      setShowForm(false);
      setForm({ email: '', password: '', first_name: '', last_name: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setCreating(false); }
  };

  const handleToggle = async (id, isActive) => {
    try {
      await superAdminApi.toggleAdmin(id);
      setAdmins(a => a.map(x => x.id === id ? { ...x, is_active: !isActive } : x));
      toast.success(isActive ? 'Admin deactivated' : 'Admin activated');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Manage Admins</h1>
          <p className="text-gray-500 text-sm">{admins.length} admins</p>
        </div>
        <button onClick={() => setShowForm(s => !s)} className="btn-primary">
          {showForm ? <FiX size={15} /> : <FiPlus size={15} />}
          {showForm ? 'Cancel' : 'New Admin'}
        </button>
      </div>

      {showForm && (
        <div className="card p-5">
          <h3 className="font-bold text-gray-900 mb-4">Create Admin Account</h3>
          <form onSubmit={handleCreate} className="grid grid-cols-2 gap-4">
            <div><label className="label">First Name</label><input className="input" required value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} /></div>
            <div><label className="label">Last Name</label><input className="input" required value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} /></div>
            <div><label className="label">Email</label><input type="email" className="input" required value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} /></div>
            <div><label className="label">Password</label><input type="password" className="input" required minLength={8} value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} /></div>
            <div className="col-span-2">
              <button type="submit" disabled={creating} className="btn-primary">
                {creating ? 'Creating...' : 'Create Admin'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b">
            <th className="table-th">Admin</th><th className="table-th">Role</th><th className="table-th">Status</th><th className="table-th text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? [...Array(3)].map((_, i) => <tr key={i}>{[...Array(4)].map((_, j) => <td key={j} className="table-td"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}</tr>)
            : admins.map(a => (
              <tr key={a.id} className="hover:bg-gray-50/50">
                <td className="table-td">
                  <div><p className="font-semibold text-sm">{a.first_name} {a.last_name}</p><p className="text-xs text-gray-400">{a.email}</p></div>
                </td>
                <td className="table-td"><span className="text-xs font-bold px-2 py-0.5 rounded-full bg-navy/10 text-navy capitalize">{a.role?.replace('_', ' ')}</span></td>
                <td className="table-td"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${a.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{a.is_active ? 'Active' : 'Disabled'}</span></td>
                <td className="table-td text-right">
                  <button onClick={() => handleToggle(a.id, a.is_active)} className="text-xl p-1.5">
                    {a.is_active ? <FiToggleRight className="text-emerald-500" /> : <FiToggleLeft className="text-gray-300" />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SuperAdminDashboard;
